import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useAuthValue } from '../../context/AuthContext';
import { useAdminValue } from '../../context/AdminContext';
import ThemeToggle from '../ui/ThemeToggle';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function NavLink({ to, onClick, children, active }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'text-sm font-medium px-3 py-2 rounded-md transition-colors duration-normal',
        active
          ? 'text-primary bg-primary-subtle'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-raised',
      )}
    >
      {children}
    </Link>
  );
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuthValue();
  const { logout } = useAuthentication();
  const { isAdmin } = useAdminValue();
  const location = useLocation();
  const drawerRef = useRef(null);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  // Sombra ao rolar
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fecha com Esc + focus trap no drawer
  useEffect(() => {
    if (!isMenuOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape') { closeMenu(); return; }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = Array.from(drawerRef.current.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    // Foca o primeiro elemento do drawer
    setTimeout(() => drawerRef.current?.querySelector(FOCUSABLE)?.focus(), 10);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen, closeMenu]);

  // Fecha ao navegar
  useEffect(() => { closeMenu(); }, [location.pathname, closeMenu]);

  const navLinks = [
    { to: '/', label: 'Agendamento', show: true },
    { to: '/controle', label: 'Admin', show: !!user && isAdmin },
    { to: '/profile', label: 'Meu Perfil', show: !!user },
    { to: '/signup', label: 'Cadastro', show: !user },
    { to: '/login', label: 'Login', show: !user },
  ];

  return (
    <>
      <header
        className={clsx(
          'sticky top-0 z-40 w-full border-b transition-shadow duration-normal',
          'bg-surface/90 backdrop-blur-md border-border',
          scrolled ? 'shadow-md' : 'shadow-none',
        )}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link
            to="/"
            className="font-logo text-2xl text-primary shrink-0 hover:opacity-80 transition-opacity duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
          >
            SALÃO LIMA
          </Link>

          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Navegação principal">
            {navLinks.filter(l => l.show).map(l => (
              <NavLink key={l.to} to={l.to} active={location.pathname === l.to}>
                {l.label}
              </NavLink>
            ))}
            {user && (
              <button
                onClick={logout}
                className="text-sm font-medium px-3 py-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-raised transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              >
                Sair
              </button>
            )}
          </nav>

          {/* Ações direita */}
          <div className="flex items-center gap-1">
            <ThemeToggle />
            {/* Hamburger (mobile) */}
            <button
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              onClick={() => setIsMenuOpen(o => !o)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Drawer mobile */}
      {isMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40 bg-overlay backdrop-blur-sm md:hidden"
            aria-hidden="true"
            onClick={closeMenu}
          />

          {/* Painel */}
          <div
            id="mobile-menu"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
            className="fixed top-0 right-0 z-50 h-full w-72 bg-surface border-l border-border shadow-xl flex flex-col md:hidden animate-drawer-in"
          >
            {/* Header do drawer */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-border shrink-0">
              <span className="font-logo text-xl text-primary">SALÃO LIMA</span>
              <button
                onClick={closeMenu}
                aria-label="Fechar menu"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              >
                <X size={20} />
              </button>
            </div>

            {/* Links */}
            <nav className="flex-1 flex flex-col gap-1 px-3 py-4 overflow-y-auto" aria-label="Menu mobile">
              {navLinks.filter(l => l.show).map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  aria-current={location.pathname === l.to ? 'page' : undefined}
                  className={clsx(
                    'text-sm font-medium px-3 py-3 rounded-md transition-colors duration-normal',
                    location.pathname === l.to
                      ? 'text-primary bg-primary-subtle'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-raised',
                  )}
                >
                  {l.label}
                </Link>
              ))}
              {user && (
                <button
                  onClick={() => { logout(); closeMenu(); }}
                  className="text-left text-sm font-medium px-3 py-3 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-raised transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                >
                  Sair
                </button>
              )}
            </nav>

            {/* Footer do drawer com toggle */}
            <div className="px-5 py-4 border-t border-border flex items-center justify-between shrink-0">
              <span className="text-xs text-text-muted">Tema</span>
              <ThemeToggle />
            </div>
          </div>
        </>
      )}
    </>
  );
}
